import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Badge,
  Pagination,
  Stack,
  CircularProgress,
  useTheme,
  alpha,
  Paper,
} from '@mui/material';
import {
  Search,
  GridView,
  List as ListIcon,
  Close,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { debounce } from 'lodash';
import Header from '../Components/Header.jsx';
import StudentSidebar from './StudentSdebar.jsx';
import { MemberCard } from '../Components/MemberPage/Member-card.jsx';

export default function MembersPage() {
  const theme = useTheme();
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      const token = localStorage.getItem('jwttoken');
      if (!token) return;

      try {
        const response = await axios.get(
          'https://tdtlworld.com/dss-kaas-backend/socialapp/users/',
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.status === 200) {
          const membersWithStatus = response.data.map((member) => ({
            ...member,
            is_friends: member.is_friends ?? false,
          }));
          setMembers(membersWithStatus);
          setFilteredMembers(membersWithStatus);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, []);

  const handleSearch = debounce((query) => {
    setSearchTerm(query);
    if (!query) {
      setFilteredMembers(members);
      return;
    }
    const lowerCaseQuery = query.toLowerCase();
    const filtered = members.filter((member) =>
      `${member.first_name} ${member.last_name}`
        .toLowerCase()
        .includes(lowerCaseQuery)
    );
    setFilteredMembers(filtered);
    setCurrentPage(1);
  }, 300);

  const indexOfLastMember = currentPage * itemsPerPage;
  const indexOfFirstMember = indexOfLastMember - itemsPerPage;
  const currentMembers = filteredMembers.slice(indexOfFirstMember, indexOfLastMember);
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
      <Header />
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <StudentSidebar onToggle={setSidebarExpanded} />
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: { xs: 2, md: 4 },
            width: sidebarExpanded ? 'calc(100% - 260px)' : 'calc(100% - 80px)',
            transition: theme.transitions.create('width', { duration: 300 }),
            height: 'calc(100vh - 64px)',
            overflowY: 'auto'
          }}
        >
          <Container maxWidth="xl">
            {/* Page Header Area */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, mb: 4 }}>
              <Box>
                <Typography variant="h3" fontWeight={800}>Community</Typography>
                <Typography variant="body1" color="text.secondary">Connect with other students and expand your network.</Typography>
              </Box>

              <TextField
                variant="outlined"
                placeholder="Search Members..."
                size="large"
                onChange={(e) => handleSearch(e.target.value)}
                sx={{ 
                  width: { xs: '100%', md: 350 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '50px',
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Navigation & Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={0} textColor="primary" indicatorColor="primary" sx={{ '& .MuiTab-root': { fontWeight: 700, px: 4 } }}>
                <Tab label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    All Members
                    <Badge badgeContent={filteredMembers.length} color="primary" sx={{ '& .MuiBadge-badge': { fontWeight: 800 } }} />
                  </Box>
                } />
              </Tabs>

              <Stack direction="row" spacing={1}>
                <IconButton onClick={() => setView('grid')} color={view === 'grid' ? 'primary' : 'inherit'} sx={{ bgcolor: view === 'grid' ? alpha(theme.palette.primary.main, 0.1) : 'transparent' }}>
                  <GridView />
                </IconButton>
                <IconButton onClick={() => setView('list')} color={view === 'list' ? 'primary' : 'inherit'} sx={{ bgcolor: view === 'list' ? alpha(theme.palette.primary.main, 0.1) : 'transparent' }}>
                  <ListIcon />
                </IconButton>
              </Stack>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
                <CircularProgress size={60} thickness={4} sx={{ color: '#D4AF37' }} />
              </Box>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={searchTerm + view}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Grid container spacing={3}>
                    {currentMembers.length > 0 ? (
                      currentMembers.map((member) => (
                        <Grid item xs={12} sm={6} md={view === 'grid' ? 4 : 12} lg={view === 'grid' ? 3 : 12} key={member.id}>
                          <MemberCard member={member} view={view} />
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 10, textAlign: 'center', borderRadius: '24px', bgcolor: alpha(theme.palette.action.disabledBackground, 0.1) }}>
                          <Typography variant="h6" color="text.secondary">No members found matching your search.</Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </motion.div>
              </AnimatePresence>
            )}

            {/* Pagination Area */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, mb: 4 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(e, v) => setCurrentPage(v)}
                  color="primary"
                  size="large"
                  sx={{
                    '& .MuiPaginationItem-root': { fontWeight: 700, fontSize: '1rem' }
                  }}
                />
              </Box>
            )}
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
